<template>
  <b-modal
      :visible="visible"
      :title="soundtrack ? `Файл: ${soundtrack.title}` : 'Файл'"
      size="lg"
      centered
      hide-footer
      @change="value => $emit('change', value)"
  >
    <div v-if="soundtrack">
      <div class="mb-1">
        <b-button
            v-for="item in sourceButtons"
            :key="item.value"
            variant="outline-secondary"
            size="sm"
            class="mr-50"
            :disabled="isSearching"
            @click="search(item.value)"
        >
          <feather-icon :icon="item.icon" class="mr-50" />
          <span>{{ item.label }}</span>
        </b-button>
      </div>

      <b-overlay :show="isSearching" rounded="sm">
        <div v-if="searched && !results.length" class="text-muted mb-1">
          Ничего не найдено
        </div>

        <b-table
            v-if="results.length"
            :items="results"
            :fields="columns"
            small
            striped
            responsive
            class="position-relative"
        >
          <template #cell(actions)="data">
            <b-button
                variant="success"
                size="sm"
                class="btn-icon"
                :disabled="isDownloading"
                @click="download(data.item)"
            >
              <feather-icon icon="DownloadIcon" size="14" />
            </b-button>
          </template>
        </b-table>
      </b-overlay>

      <hr>

      <div class="d-flex align-items-center">
        <b-form-file
            v-model="uploadFile"
            accept="audio/mpeg"
            placeholder="Выберите mp3"
            browse-text="Обзор"
            class="mr-1"
        />
        <b-button variant="primary" size="sm" :disabled="!uploadFile || isUploading" @click="upload">
          <b-spinner v-if="isUploading" small class="mr-50" />
          <span>Загрузить</span>
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BTable, BOverlay, BSpinner, BFormFile,
} from 'bootstrap-vue'
import { ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

const SOURCE_BUTTONS = [
  { value: 'yandex', label: 'Яндекс.Музыка', icon: 'MusicIcon' },
  { value: 'vk', label: 'ВК', icon: 'UsersIcon' },
]

export default {
  components: {
    BModal, BButton, BTable, BOverlay, BSpinner, BFormFile,
  },
  props: {
    movieId: { type: [String, Number], required: true },
    soundtrack: { type: Object, default: null },
    visible: { type: Boolean, default: false },
  },
  model: { prop: 'visible', event: 'change' },
  setup(props, { emit }) {
    const toast = useToast()

    const source = ref(null)
    const results = ref([])
    const searched = ref(false)
    const isSearching = ref(false)
    const isDownloading = ref(false)
    const isUploading = ref(false)
    const uploadFile = ref(null)

    // Ответы ВК и Яндекса различаются по форме, поэтому колонки общие и мягкие.
    const columns = [
      { key: 'title', label: 'Название', thStyle: 'min-width: 240px' },
      { key: 'artist', label: 'Исполнитель', thStyle: 'min-width: 160px' },
      { key: 'actions', label: '', thStyle: 'width: 60px' },
    ]

    const notifyError = (error, fallback) => {
      toast({
        component: ToastificationContent,
        props: {
          title: (error.response && error.response.data && error.response.data.message) || fallback,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }

    const normalize = (rows, sourceValue) => {
      const list = Array.isArray(rows) ? rows : (rows && rows.items) || []

      return list.map(row => ({
        id: sourceValue === 'vk' ? `${row.owner_id}_${row.id}` : String(row.id),
        title: row.title,
        artist: row.artist,
      }))
    }

    const search = value => {
      source.value = value
      isSearching.value = true
      searched.value = false

      store
        .dispatch('movies/searchMovieSoundtrackAudio', {
          id: props.movieId,
          soundtrackId: props.soundtrack.id,
          source: value,
        })
        .then(response => {
          results.value = normalize(response.data.results, value)
          searched.value = true

          if (response.data.error) {
            notifyError({ response: { data: { message: response.data.error } } }, 'Источник вернул ошибку')
          }
        })
        .catch(error => notifyError(error, 'Не удалось выполнить поиск'))
        .finally(() => { isSearching.value = false })
    }

    const download = item => {
      isDownloading.value = true
      store
        .dispatch('movies/downloadMovieSoundtrackAudio', {
          id: props.movieId,
          soundtrackId: props.soundtrack.id,
          source: source.value,
          externalId: item.id,
        })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: { title: 'Файл скачан', icon: 'CheckIcon', variant: 'success' },
          })
          emit('updated')
          emit('change', false)
        })
        .catch(error => notifyError(error, 'Не удалось скачать файл'))
        .finally(() => { isDownloading.value = false })
    }

    const upload = () => {
      isUploading.value = true
      store
        .dispatch('movies/uploadMovieSoundtrackFile', {
          id: props.movieId,
          soundtrackId: props.soundtrack.id,
          file: uploadFile.value,
        })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: { title: 'Файл загружен', icon: 'CheckIcon', variant: 'success' },
          })
          uploadFile.value = null
          emit('updated')
          emit('change', false)
        })
        .catch(error => notifyError(error, 'Не удалось загрузить файл'))
        .finally(() => { isUploading.value = false })
    }

    watch(() => props.visible, value => {
      if (!value) {
        source.value = null
        results.value = []
        searched.value = false
        uploadFile.value = null
      }
    })

    return {
      sourceButtons: SOURCE_BUTTONS,
      results,
      searched,
      isSearching,
      isDownloading,
      isUploading,
      uploadFile,
      columns,
      search,
      download,
      upload,
    }
  },
}
</script>
